import React, { useEffect, useState } from 'react';
import { useAuth } from '../lib/AuthContext';
import supabase from '../lib/supabase';

const S = {
  page: { minHeight: 'calc(100vh - 56px)', background: '#0f1115', padding: '32px 16px', display: 'flex', justifyContent: 'center' },
  wrap: { width: '100%', maxWidth: '640px' },
  title: { fontSize: '22px', fontWeight: 800, color: '#fff', marginBottom: '24px' },
  card: { background: '#1a1d24', borderRadius: '12px', padding: '24px', border: '1px solid #2a2d35', marginBottom: '16px' },
  cardTitle: { fontSize: '13px', color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '16px' },
  row: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', fontSize: '14px' },
  rowLabel: { color: '#6b7280' },
  rowVal: { color: '#fff', fontWeight: 600 },
  rowGreen: { color: '#00e701', fontWeight: 600 },
  rowRed: { color: '#ff4444', fontWeight: 600 },
  histRow: { borderBottom: '1px solid #2a2d35', paddingBottom: '10px', marginBottom: '10px', fontSize: '13px' },
  histMeta: { display: 'flex', justifyContent: 'space-between', marginBottom: '4px' },
  histSub: { color: '#6b7280', fontSize: '11px' },
  badge: { display: 'inline-block', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 },
  badgeWin: { background: 'rgba(0,231,1,0.1)', color: '#00e701' },
  badgeLoss: { background: 'rgba(255,32,32,0.1)', color: '#ff4444' },
  toggleWrap: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #2a2d35' },
  toggleInfo: { flex: 1 },
  toggleLabel: { fontSize: '14px', color: '#fff', fontWeight: 600, marginBottom: '4px' },
  toggleDesc: { fontSize: '12px', color: '#6b7280' },
  toggle: { position: 'relative', width: '48px', height: '24px', borderRadius: '12px', background: '#2a2d35', cursor: 'pointer', transition: 'background 0.2s', border: 'none', padding: 0 },
  toggleActive: { background: '#00e701' },
  toggleKnob: { position: 'absolute', top: '2px', left: '2px', width: '20px', height: '20px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' },
  toggleKnobActive: { left: '26px' },
};

export default function Profile() {
  const { profile, refreshProfile } = useAuth();
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hideStats, setHideStats] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!profile) return;
    setHideStats(profile.hide_stats || false);
    loadData();
  }, [profile]);

  async function loadData() {
    if (!profile) return;
    const { data: g } = await supabase
      .from('games')
      .select('*')
      .eq('user_id', profile.id)
      .order('created_at', { ascending: false })
      .limit(20);

    setGames(g || []);

    const totalWagered = (g || []).reduce((s, x) => s + Number(x.bet_amount), 0);
    const wins = (g || []).filter(x => x.won);
    const losses = (g || []).filter(x => !x.won);
    const totalPayout = wins.reduce((s, x) => s + Number(x.payout), 0);
    const totalLost = losses.reduce((s, x) => s + Number(x.bet_amount), 0);

    setStats({
      games: (g || []).length,
      wins: wins.length,
      losses: losses.length,
      wagered: totalWagered,
      profit: totalPayout - totalLost,
    });
    setLoading(false);
  }

  async function toggleHideStats() {
    setSaving(true);
    const newValue = !hideStats;
    setHideStats(newValue);

    await supabase
      .from('users')
      .update({ hide_stats: newValue })
      .eq('id', profile.id);

    await refreshProfile();
    setSaving(false);
  }

  if (!profile) return null;

  return (
    <div style={S.page}>
      <div style={S.wrap}>
        <div style={S.title}>👤 Profile</div>

        <div style={S.card}>
          <div style={S.cardTitle}>Account</div>
          <div style={S.row}>
            <span style={S.rowLabel}>Username</span>
            <span style={S.rowVal}>{profile.username}</span>
          </div>
          <div style={S.row}>
            <span style={S.rowLabel}>Balance</span>
            <span style={S.rowGreen}>₿{Number(profile.balance || 0).toFixed(2)}</span>
          </div>
          <div style={S.row}>
            <span style={S.rowLabel}>Member since</span>
            <span style={S.rowVal}>{new Date(profile.created_at).toLocaleDateString()}</span>
          </div>
        </div>

        {stats && (
          <div style={S.card}>
            <div style={S.cardTitle}>Stats</div>
            <div style={S.row}><span style={S.rowLabel}>Total games</span><span style={S.rowVal}>{stats.games}</span></div>
            <div style={S.row}><span style={S.rowLabel}>Wins</span><span style={S.rowGreen}>{stats.wins}</span></div>
            <div style={S.row}><span style={S.rowLabel}>Losses</span><span style={S.rowRed}>{stats.losses}</span></div>
            <div style={S.row}><span style={S.rowLabel}>Total wagered</span><span style={S.rowVal}>₿{stats.wagered.toFixed(2)}</span></div>
            <div style={S.row}>
              <span style={S.rowLabel}>Net profit</span>
              <span style={stats.profit >= 0 ? S.rowGreen : S.rowRed}>{stats.profit >= 0 ? '+' : ''}₿{stats.profit.toFixed(2)}</span>
            </div>
          </div>
        )}

        <div style={S.card}>
          <div style={S.cardTitle}>Settings</div>
          <div style={S.toggleWrap}>
            <div style={S.toggleInfo}>
              <div style={S.toggleLabel}>Hide Profile Stats</div>
              <div style={S.toggleDesc}>When enabled, other users won't see your stats in chat</div>
            </div>
            <button
              style={{
                ...S.toggle,
                ...(hideStats ? S.toggleActive : {}),
              }}
              onClick={toggleHideStats}
              disabled={saving}
            >
              <div style={{
                ...S.toggleKnob,
                ...(hideStats ? S.toggleKnobActive : {}),
              }} />
            </button>
          </div>
        </div>

        <div style={S.card}>
          <div style={S.cardTitle}>Recent Games</div>
          {loading && <div style={{ color: '#6b7280', fontSize: '13px' }}>Loading...</div>}
          {!loading && games.length === 0 && <div style={{ color: '#6b7280', fontSize: '13px' }}>No games yet. Go play!</div>}
          {games.map(g => (
            <div key={g.id} style={S.histRow}>
              <div style={S.histMeta}>
                <span style={{ color: '#fff', fontWeight: 600 }}>₿{Number(g.bet_amount).toFixed(2)} bet · {g.mine_count} mines</span>
                <span style={{ ...S.badge, ...(g.won ? S.badgeWin : S.badgeLoss) }}>{g.won ? `+₿${Number(g.payout - g.bet_amount).toFixed(2)}` : `-₿${Number(g.bet_amount).toFixed(2)}`}</span>
              </div>
              <div style={S.histSub}>{g.gems_revealed} gems · {Number(g.multiplier).toFixed(2)}x · {new Date(g.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
